/**
 * Design tokens for the generated-course player.
 *
 * These mirror the LMS chapter player in NSCL-web
 * (nscl/src/app/components/chapter/chapterStyles.ts) so a generated course
 * reads as the same product as a hand-authored LMS book. Values are copied
 * from there rather than re-derived; the palette in particular is quoted
 * exactly.
 *
 * Kept as a local file rather than imported from NSCL because the two apps do
 * not share a package — this is the whole of the contract, in one place.
 */
export const F = {
  teal: '#3DAB9E', // page background
  tealDark: '#2B786F', // teal button bottom-border
  brown: '#4D3F37', // body text
  brownDark: '#262322', // headings
  white: '#FFFFFF',
  yellow: '#FACF47', // primary CTA
  yellowBorder: '#A9873B', // CTA bottom-border
  muted: '#A8A7A6', // borders and disabled fills only — 2.4:1 on white
  // Muted *text*. #A8A7A6 measures 2.4:1 on white, well under the 4.5:1 AA
  // needs, and it was labelling things students have to read (stat captions,
  // chapter counts, the moves label). This is 4.6:1.
  mutedText: '#6E6B69',
  blue: '#1CB0F6', // links
  border10: 'rgba(38,35,34,0.1)',
  // Feedback accents, also from the LMS player.
  wrongBg: '#FBDDDD',
  wrongFg: '#EB5757',
  badgeBg: '#F8F3E6',
  // Hint card, from the LMS player's own hint treatment.
  hintBg: 'rgba(225,120,70,0.12)',
  hintFg: '#E17846',
  radius: {card: 16, button: 10},
} as const;

export const CARD_SHADOW = {
  shadowColor: '#000',
  shadowOffset: {width: 0, height: 8},
  shadowOpacity: 0.08,
  shadowRadius: 24,
  elevation: 6,
} as const;

/**
 * The LMS player switches to a single stacked column below this width. Matching
 * the breakpoint keeps the two players behaving the same on a narrow window.
 */
export const MOBILE_BREAKPOINT = 620;

/** Width of the app's own left navigation on a wide window. */
export const APP_NAV_WIDTH = 160;

/**
 * Width actually available to a screen, i.e. the window minus the app's left
 * nav. Layout decisions must use this rather than the raw window width: the
 * nav is ~160px, so a 700px window leaves ~540px of content, and sizing a
 * board at `windowWidth * 0.42` there left the lesson card with under 180px.
 */
export function contentWidth(windowWidth: number): number {
  if (!Number.isFinite(windowWidth) || windowWidth <= 0) return 0;
  // Below the breakpoint the app hides its nav, so the whole window is content.
  if (windowWidth < MOBILE_BREAKPOINT) return windowWidth;
  return Math.max(MOBILE_BREAKPOINT, windowWidth - APP_NAV_WIDTH);
}

/** Narrowest the lesson card may get before the rail is doing more harm than good. */
export const MIN_LESSON_CARD = 280;

/** The coach rail's width, as a share of the content area within bounds. */
export function coachRailWidth(windowWidth: number): number {
  const available = contentWidth(windowWidth);
  return Math.max(320, Math.min(420, Math.round(available * 0.3)));
}

/**
 * Where the coach should live at this width: docked beside the lesson, or as a
 * sheet across the bottom.
 *
 * The board does not shrink to make room — `boardSize` is derived from the
 * window, not from its container — so the lesson card is the only thing that
 * gives way when the rail opens. Below `MIN_LESSON_CARD` the text becomes a
 * ribbon, and a sheet is the better trade.
 */
export function coachLayout(
  windowWidth: number,
  boardSize: number,
): 'rail' | 'sheet' {
  const available = contentWidth(windowWidth);
  if (available < MOBILE_BREAKPOINT) return 'sheet';
  // 48 of page padding either side of the content, 24 of gap beside the board.
  const lessonCard =
    available - coachRailWidth(windowWidth) - 48 - 24 - (boardSize || 0);
  return lessonCard >= MIN_LESSON_CARD ? 'rail' : 'sheet';
}
